import { useCallback, useEffect, useRef } from 'react';

import { useFocusEffect } from 'expo-router';
import fastDeepEqual from 'fast-deep-equal';
import { useLoyaltyUserQuery } from 'features/loyalty-user/use-loyalty-user-query';

import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { updateLaunchDarklyUser } from 'state/launchdarkly/launchdarkly-core';

import type { IUseLoyaltyUserState } from './types';
import { useIsLoyaltyEnabled } from './use-is-loyalty-enabled';
import { useLoyaltyUserId } from './use-loyalty-user';

/**
  Get loyalty user state (Rewards Points etc.) fresh or from redux. will always populate redux with latest.
  ⚠️ Just need loyaltyId? use useLoyaltyUserId hook ⚠️
 */
export const useLoyaltyUserState = ({
  refreshOnMountOrFocus,
  onSuccess,
}: {
  /** ensure loyalty info isn't stale */
  refreshOnMountOrFocus: boolean;
  onSuccess?: Function;
}): IUseLoyaltyUserState => {
  const dispatch = useAppDispatch();
  const { loyaltyUser: cacheLoyaltyUser, loading: cacheIsLoading } = useAppSelector(
    selectors.loyalty.selectLoyaltyUser
  );
  const { loyaltyUserId: loyaltyId } = useLoyaltyUserId();
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const loyaltyIdentityRef = useRef(loyaltyId);
  const cacheLoyaltyUserRef = useRef(cacheLoyaltyUser);
  const refreshRequestId = useRef(0);
  loyaltyIdentityRef.current = loyaltyId;
  cacheLoyaltyUserRef.current = cacheLoyaltyUser;

  // TODO: move this to offer's context
  const offersCooldownEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_GLOBAL_OFFERS_COOLDOWN));

  const {
    called,
    error,
    fetchLoyaltyUser,
    loading: localLoading,
    loyaltyUser: queriedLoyaltyUser,
    source,
  } = useLoyaltyUserQuery();
  const previousSourceRef = useRef(source);
  const sourceChanged = previousSourceRef.current !== source;

  // Mimic useEvent for callback passed as props
  // Refer to: https://github.com/reactjs/rfcs/blob/useevent/text/0000-useevent.md
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  // Load if loyalty is enabled and a loyaltyId exists. If refreshOnMountOrFocus is true, always fetch fresh data; otherwise, fetch only if there is no cached loyalty user and the query hasn't been called yet.
  const cacheMatchesIdentity = Boolean(loyaltyId && cacheLoyaltyUser?.id === loyaltyId);
  const shouldLoad = Boolean(
    loyaltyEnabled &&
      loyaltyId &&
      !sourceChanged &&
      (refreshOnMountOrFocus || (!cacheMatchesIdentity && !called))
  );

  const refresh: IUseLoyaltyUserState['refetch'] = useCallback(
    async variables => {
      const effectiveLoyaltyId = variables?.loyaltyId ?? loyaltyIdentityRef.current;
      if (!effectiveLoyaltyId) {
        return undefined;
      }

      const requestId = refreshRequestId.current + 1;
      refreshRequestId.current = requestId;
      dispatch(actions.loyalty.setLoyaltyLoading(true));
      try {
        const loyaltyUser = await fetchLoyaltyUser({ loyaltyId: effectiveLoyaltyId });
        if (
          loyaltyUser === undefined ||
          effectiveLoyaltyId !== loyaltyIdentityRef.current ||
          (loyaltyUser !== null && loyaltyUser.id !== loyaltyIdentityRef.current)
        ) {
          return undefined;
        }

        if (!fastDeepEqual(loyaltyUser, cacheLoyaltyUserRef.current)) {
          dispatch(actions.loyalty.setUser(loyaltyUser));
        }

        const offerRedemptionAvailableAfter =
          loyaltyUser?.offerRedemptionAvailability?.availableAfter;
        if (offerRedemptionAvailableAfter && offersCooldownEnabled) {
          dispatch(actions.loyalty.setOfferRedemptionAvailableAfter(offerRedemptionAvailableAfter));
        }

        updateLaunchDarklyUser(
          {
            loyaltyId: loyaltyUser?.id,
          },
          'loyalty user id change'
        );

        if (onSuccessRef.current) {
          onSuccessRef.current();
        }
        return loyaltyUser;
      } finally {
        if (refreshRequestId.current === requestId) {
          dispatch(actions.loyalty.setLoyaltyLoading(false));
        }
      }
    },
    [dispatch, fetchLoyaltyUser, offersCooldownEnabled]
  );

  useEffect(() => {
    if (!sourceChanged || !loyaltyEnabled || !loyaltyId) {
      return;
    }

    previousSourceRef.current = source;
    void refresh().catch(() => undefined);
  }, [loyaltyEnabled, loyaltyId, refresh, source, sourceChanged]);

  const loadLoyaltyUser = useCallback(() => {
    if (shouldLoad) {
      void refresh().catch(() => undefined);
    }
  }, [shouldLoad, refresh]);

  // for on mount
  useEffect(() => {
    loadLoyaltyUser();
  }, [loadLoyaltyUser]);

  // for when expo router refocuses the already loaded screen
  useFocusEffect(loadLoyaltyUser);

  const loyaltyUser =
    called && queriedLoyaltyUser === null
      ? null
      : queriedLoyaltyUser?.id === loyaltyId
      ? queriedLoyaltyUser
      : cacheMatchesIdentity
      ? cacheLoyaltyUser
      : null;

  const loading = localLoading || cacheIsLoading;

  return {
    loyaltyUser,
    error,
    refetch: refresh,
    loading,
  };
};
